import { useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Sparkles, Check, Loader2, Zap, Star } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { toast } from 'sonner';
import { useDashboard } from '@/context/DashboardContext';
import { fmtMoney } from '@/lib/format';
import { BillingChannelPicker, LockedChannelNote, needsBillingChoice, lockedChannel } from '@/components/dashboard/BillingChannelPicker';

// Tarifa vigente desde el 20/09/2026: las tres membresías son anuales.
// Access y PRO solo se diferencian en el nivel de Academia y la aportación mínima por operación.
const FEATURES = {
  starter: [
    'Acceso a FHC Academy (nivel base)',
    'Oportunidades inmobiliarias del club',
    'Newsletter mensual e informes del club',
    'Comunidad de socios y networking internacional',
    'Soporte por ticket: respuesta en un máximo de 48 h laborables',
    'Aportación mínima por operación: 5.000 $',
  ],
  basica: [
    'Todo lo de Access',
    'Acceso avanzado a FHC Academy',
    'Eventos online y sesiones formativas en directo',
    'Soporte por ticket: respuesta en un máximo de 24 h laborables',
    'Aportación mínima por operación: 1.000 $',
    'Opción de plaza vitalicia tras la compra',
  ],
  premium: [
    'Todo lo de PRO',
    'Bootcamp Broker Digital: 4 días presenciales (alojamiento y dietas no incluidos)',
    'Certificación FHC como Broker Digital en Tokenización Inmobiliaria',
    'Habilitación para comercializar el modelo FHC como broker certificado',
    'Calculadora de red: simula tus comisiones y bricks por nivel',
    'Opción de plaza vitalicia tras la compra',
  ],
};

// Jerarquía: starter(1) < basica(2) < premium(3) — para calcular isLocked
const RANK = { starter: 1, basica: 2, premium: 3 };

export function MembershipCard({ plan, currentMembership, currency = 'usd', upgrade = null, delay = 0 }) {
  const { membership } = useDashboard();
  const billing = membership?.billing;
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const [busyChannel, setBusyChannel] = useState(null);

  const isStarter = plan.id === 'starter';
  const isPremium = plan.id === 'premium';
  const isPro = plan.id === 'basica';
  const isSubscription = plan.billing === 'annual';

  const isOwned = currentMembership === plan.id;
  // Locked = el usuario ya tiene un plan superior, este no le aporta nada
  const isLocked =
    !!currentMembership &&
    !isOwned &&
    (RANK[currentMembership] || 0) > (RANK[plan.id] || 0);

  // Moneda por región (T&C 8.5): EUR en Europa, USD fuera. El backend decide y cobra.
  // La del upgrade manda (es la que se cobra); si no, la del usuario.
  const cur = String(upgrade?.currency || billing?.currency || currency || 'usd').toLowerCase() === 'eur' ? 'eur' : 'usd';
  const isEur = cur === 'eur' && !!plan.price_eur_cents;
  const money = (cents) => fmtMoney(cents / 100, cur);
  // Upgrade (Access → PRO/Premium, PRO → Premium): diferencia los 30 primeros días, después precio fijo
  const fromName = { starter: 'Access', basica: 'PRO' }[upgrade?.from] || '';
  const priceCents = upgrade ? upgrade.amount_cents : (isEur ? plan.price_eur_cents : plan.price_cents);
  const upgradeUntil = upgrade?.early && upgrade.deadline
    ? new Date(upgrade.deadline).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }) : null;
  // Un upgrade de quien tiene una anual se cobra por la misma vía que esa suscripción
  const locked = upgrade ? lockedChannel(billing) : null;

  const goToStripe = async (channel) => {
    setLoading(true);
    setBusyChannel(channel);
    try {
      const { checkout_url } = await dashboardApi.createCheckout(plan.id, channel);
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.message || 'No se pudo iniciar el pago');
      setLoading(false);
      setBusyChannel(null);
    }
  };

  const handlePurchase = () => {
    if (needsBillingChoice(billing, cur, !!locked)) setPicking(true);
    else goToStripe(locked || 'particular');
  };

  // ── Estilos por tier ──
  const borderClass = isPremium
    ? 'border-[#c9a84c]/30 bg-gradient-to-br from-[#c9a84c]/[0.06] to-white/[0.02]'
    : isStarter
    ? 'border-[#8b5cf6]/20 bg-white/[0.02]'
    : 'border-white/[0.08] bg-white/[0.02]';

  const TierIcon = isPremium ? Crown : isPro ? Star : Zap;
  const iconColor = isPremium
    ? 'text-[#c9a84c]'
    : isPro
    ? 'text-[#c9a84c]'
    : 'text-[#a78bfa]';

  const ctaClass = isPremium
    ? 'bg-[#c9a84c] text-[#0a1628] hover:bg-[#d4b357]'
    : isPro
    ? 'bg-white/[0.06] text-white border border-[#c9a84c]/30 hover:bg-white/[0.1]'
    : 'bg-white/[0.04] text-white border border-white/[0.08] hover:bg-white/[0.08]';

  const subtitle = upgrade
    ? (upgrade.keeps_lifetime ? `Upgrade desde ${fromName} · Mantienes tu condición vitalicia` : `Upgrade desde ${fromName} · Pago único`)
    : isSubscription
    ? 'Membresía anual · Renovación automática'
    : 'Membresía vitalicia';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      className={`relative rounded-2xl border p-6 ${borderClass}`}
    >
      {isPremium && (
        <div className="absolute -top-3 right-6 bg-[#c9a84c] text-[#0a1628] text-[11px] uppercase tracking-wider font-bold px-3 py-1 rounded-full flex items-center gap-1">
          <Sparkles size={10} /> Recomendado
        </div>
      )}

      <div className="flex items-center gap-2 mb-1">
        <TierIcon size={16} className={iconColor} />
        <h3 className="text-white font-bold text-lg">{plan.name}</h3>
      </div>
      <p className="text-xs text-gray-400 mb-4">{subtitle}</p>

      <div className="mb-5">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-white">{money(priceCents)}</span>
          {isSubscription && !upgrade && (
            <span className="text-sm text-gray-400">/año</span>
          )}
        </div>
        {upgrade ? (
          <p className="text-[11px] text-[#c9a84c] uppercase tracking-wider mt-1">
            {upgradeUntil ? `Precio de upgrade hasta el ${upgradeUntil}`
              : (upgrade.from === 'basica' ? 'Bootcamp incluido: lo que cuesta la plaza suelta' : 'Precio de upgrade')}
          </p>
        ) : (
          <p className="text-[11px] text-gray-400 uppercase tracking-wider mt-1">
            Cancela la renovación cuando quieras
          </p>
        )}
        {locked && (
          <LockedChannelNote billing={billing} amount={priceCents / 100} currency={cur} className="mt-2" />
        )}
      </div>

      <ul className="space-y-2 mb-6">
        {(FEATURES[plan.id] || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
            <Check
              size={14}
              className={`mt-0.5 shrink-0 ${isStarter ? 'text-[#a78bfa]' : 'text-[#c9a84c]'}`}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isOwned ? (
        <div className="w-full py-3 rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/30 text-[#c9a84c] text-sm font-semibold text-center">
          ✓ Membresía activa
        </div>
      ) : isLocked ? (
        <div className="w-full py-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-gray-400 text-sm text-center">
          Incluida en tu plan actual
        </div>
      ) : (
        <button
          onClick={handlePurchase}
          disabled={loading}
          className={`w-full py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed ${ctaClass}`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" />
              Procesando...
            </span>
          ) : upgrade ? (
            `Pasar a ${plan.name}`
          ) : isSubscription ? (
            'Suscribirme'
          ) : (
            'Comprar ahora'
          )}
        </button>
      )}

      <BillingChannelPicker
        open={picking}
        billing={billing}
        amount={priceCents / 100}
        currency={cur}
        period={isSubscription && !upgrade ? '/ año' : null}
        title={upgrade ? `Pasar a ${plan.name}` : `Membresía ${plan.name}`}
        subtitle="¿Cómo quieres pagar?"
        busyChannel={busyChannel}
        onSelect={goToStripe}
        onClose={() => { if (!busyChannel) setPicking(false); }}
      />
    </motion.div>
  );
}
